import React, { useEffect, useCallback, useRef, useState, TouchEvent } from "react";
import * as H from "./style";
import Post from "../../Components/Post/Post";
import Suggesstion from "../../Components/Suggesstion";
import CreatorSkeleton from "../../Components/Skeletons/CreatorSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../Redux/store";
import { checkIsSubscribe, isCreatorRole } from "../../Config/common.config";
import { RoleTypes } from "../../Constant/enums/roleTypes";
import {
  clearExploreResults,
  fetchExploreList,
} from "../../Redux/slices/exploreSlice";
import { feedHandler } from "../../Redux/slices/postSlice";
import ProfileHeader from "../../Components/Profile/ProfileHeader";
import SubscriptionModal from "../../Modals/SubscriptionModal";
import PostSkeleton from "../../Components/Post/PostSkeleton";
import { UserDetails } from "../../Config/interface.config";
import CreatorOnboardingChecklist from "../../Components/CreatorOnboardingChecklist";
import CreatePostModal from "../../Modals/CreatePostModal";

const Home: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

  /* ---------------- REDUX ---------------- */
  const { feed, loading: feedLoading } = useSelector((s: RootState) => s.posts);
  const user = useSelector((s: RootState) => s.user);
  const explore = useSelector((s: RootState) => s.explore);

  /* ---------------- REFS ---------------- */
  const feedRootRef = useRef<HTMLDivElement | null>(null);
  const feedSentinelRef = useRef<HTMLDivElement | null>(null);
  const pullStartYRef = useRef<number | null>(null);
  const pullDistanceRef = useRef(0);

  const creatorRootRef = useRef<HTMLDivElement | null>(null);
  const creatorSentinelRef = useRef<HTMLDivElement | null>(null);

  const initRef = useRef(false);
  const creatorsInitRef = useRef(false);

  /* ---------------- STATE ---------------- */
  const [open, setOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<any>(null);
  const [createPostModal, setCreatePostModal] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshingFeed, setRefreshingFeed] = useState(false);
  const approvedCreator = isCreatorRole(user.role) && user.isApproved === true;
  const creatorPostCount = Math.max(
    Number(user.totalPost ?? 0),
    feed.results.filter(
      (post) => post?.creator?.id === user.id || post?.creator?._id === user.id,
    ).length,
  );
  const showCreatorOnboarding = approvedCreator && creatorPostCount === 0;
  const suggestedCreators = explore.results.filter(
    (creator) =>
      creator?.isSuggested !== false &&
      !checkIsSubscribe(creator?._id || creator?.id || "", user.subscribeTo as any),
  );
  const onboardingState = {
    profileComplete: Boolean(
      user.image?.fileURL && user.backgroundImage?.fileURL && user.bio?.trim(),
    ),
    membershipComplete: Boolean(
      user.memberDetails?.monthlyPrice && user.memberDetails?.messagePrice,
    ),
    firstPostComplete: creatorPostCount > 0,
    shareComplete: Boolean(user.username),
    username: user.username,
  };

  /* ================= FEED ================= */

  const fetchFeed = useCallback(
    (page: number) => {
      if (!user?.id) return;
      if (feedLoading) return;
      if (!feed.hasMore && page !== 1) return;

      dispatch(
        feedHandler({
          limit: 10,
          page,
          isIntro: false,
        }),
      );
    },
    [dispatch, user?.id, feedLoading, feed.hasMore],
  );

  const refreshFeed = useCallback(async () => {
    if (!user?.id || feedLoading || refreshingFeed) return;

    setRefreshingFeed(true);
    try {
      await dispatch(feedHandler({ limit: 10, page: 1, isIntro: false }));
    } finally {
      setRefreshingFeed(false);
    }
  }, [dispatch, user?.id, feedLoading, refreshingFeed]);

  const handleFeedTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (feedRootRef.current?.scrollTop === 0 && !feedLoading && !refreshingFeed) {
      pullStartYRef.current = event.touches[0]?.clientY ?? null;
    }
  };

  const handleFeedTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (pullStartYRef.current === null || feedRootRef.current?.scrollTop !== 0) return;

    const distance = (event.touches[0]?.clientY ?? pullStartYRef.current) - pullStartYRef.current;
    if (distance <= 0) return;

    const nextDistance = Math.min(84, Math.round(distance * 0.42));
    pullDistanceRef.current = nextDistance;
    setPullDistance(nextDistance);
  };

  const handleFeedTouchEnd = () => {
    const shouldRefresh = pullDistanceRef.current >= 56;
    pullStartYRef.current = null;
    pullDistanceRef.current = 0;
    setPullDistance(0);

    if (shouldRefresh) void refreshFeed();
  };

  /* ================= CREATORS ================= */

  const fetchCreators = useCallback(
    (page: number, append = false) => {
      if (explore.loading) return;
      if (!explore.hasMore && page !== 1) return;

      dispatch(
        fetchExploreList({
          limit: 10,
          page,
          isAppend: append,
          isFilterBySubscriber: true,
          role: RoleTypes.CREATOR,
        }),
      );
    },
    [dispatch, explore.loading, explore.hasMore],
  );

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    if (!user?.id) return;
    if (initRef.current) return;

    initRef.current = true;

    dispatch(clearExploreResults());
    fetchFeed(1);
    fetchCreators(1);
    creatorsInitRef.current = true;
  }, [dispatch, user?.id, fetchFeed, fetchCreators]);

  /* ================= FEED INFINITE SCROLL ================= */

  useEffect(() => {
    const root = feedRootRef.current;
    const target = feedSentinelRef.current;

    if (!root || !target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchFeed(feed.page + 1);
        }
      },
      {
        root,
        rootMargin: "300px",
        threshold: 0.1,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [feed.page, fetchFeed]);

  /* ================= CREATOR INFINITE SCROLL ================= */

  useEffect(() => {
    if (!creatorsInitRef.current) return;

    const root = creatorRootRef.current;
    const target = creatorSentinelRef.current;

    if (!root || !target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && explore.hasMore && !explore.loading) {
          fetchCreators(explore.page + 1, true);
        }
      },
      {
        root,
        rootMargin: "200px",
        threshold: 0.1,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [explore.page, explore.hasMore, explore.loading, fetchCreators]);

  if (!user) return null;

  return (
    <H.HomeWrapper>
      {/* ================= FEED ================= */}
      <H.Feed>
        <H.FeedWrapper
          ref={feedRootRef}
          stretch={approvedCreator}
          onTouchStart={handleFeedTouchStart}
          onTouchMove={handleFeedTouchMove}
          onTouchEnd={handleFeedTouchEnd}
          onTouchCancel={handleFeedTouchEnd}
        >
          <H.PullToRefreshIndicator $offset={pullDistance} $refreshing={refreshingFeed}>
            {refreshingFeed ? "Refreshing feed..." : pullDistance >= 56 ? "Release to refresh" : "Pull down to refresh"}
          </H.PullToRefreshIndicator>
          {approvedCreator && <ProfileHeader user={user as UserDetails} />}

          <div className="feed-box">
            {showCreatorOnboarding && !feedLoading && (
              <CreatorOnboardingChecklist
                state={onboardingState}
                onCreatePost={() => setCreatePostModal(true)}
              />
            )}

            {feed.results
              .filter((post) => post && post.creator)
              .map((post) => (
                <Post
                  key={post.id || post._id}
                  post={post}
                  isSubscribed={post.isLocked === false}
                />
              ))}

            {feedLoading &&
              Array.from({ length: 3 }).map((_, i) => <PostSkeleton key={i} />)}

            {/* FEED SENTINEL */}
            <div ref={feedSentinelRef} style={{ height: 40 }} />
          </div>
        </H.FeedWrapper>
      </H.Feed>

      {/* ================= SIDEBAR ================= */}
      {!approvedCreator && (
        <H.SuggestionWrapper>
          <H.SuggestedTitle>Suggested For You</H.SuggestedTitle>

          <H.SuggestedContentWrapper ref={creatorRootRef}>
            {suggestedCreators.map((creator) => (
              <Suggesstion
                key={creator.id}
                creator={creator}
                subscribeCreator={(c) => {
                  setSelectedCreator(c);
                  setOpen(true);
                }}
              />
            ))}

            {explore.loading &&
              Array.from({ length: 3 }).map((_, i) => (
                <CreatorSkeleton key={i} />
              ))}

            {/* CREATOR SENTINEL */}
            <div ref={creatorSentinelRef} style={{ height: 40 }} />
          </H.SuggestedContentWrapper>
        </H.SuggestionWrapper>
      )}

      <SubscriptionModal
        selectedCreator={selectedCreator}
        show={open}
        onHide={() => setOpen(false)}
        setRefreshFeedsWithCreatorList={() => {}}
      />
      <CreatePostModal
        onHide={() => setCreatePostModal(false)}
        show={createPostModal}
        key="home-onboarding-create-post-modal"
      />
    </H.HomeWrapper>
  );
};

export default Home;
